import type { PluginMetadata, PluginObjectType } from "./protocol.js"
import { PLUGIN_METADATA_MAX_PX, PLUGIN_OBJECT_TYPES } from "./protocol.js"

/**
 * The one set of rules for `livespace-plugin:metadata`, shared by both ends —
 * as `sanitizeModalSize` is for the modal's bounds.
 *
 * The SDK runs it before posting, so an author is told locally what was wrong;
 * the host runs it again on arrival, because the payload comes from a
 * third-party iframe. Built field by field, never spread, so a foreign key
 * cannot ride along. Lenient per field: one bad value does not cost the rest.
 */
export type SanitizedPluginMetadata = {
	metadata: PluginMetadata
	/** The keys that were present but unusable — for the SDK's warning. */
	dropped: (keyof PluginMetadata)[]
}

const DIMENSIONS = ["minWidthPx", "minHeightPx"] as const

const isUsableDimension = (value: unknown): value is number =>
	typeof value === "number" &&
	Number.isFinite(value) &&
	value >= 1 &&
	value <= PLUGIN_METADATA_MAX_PX

const isObjectType = (value: unknown): value is PluginObjectType =>
	(PLUGIN_OBJECT_TYPES as readonly unknown[]).includes(value)

export const sanitizePluginMetadata = (
	raw: unknown,
): SanitizedPluginMetadata => {
	const result: SanitizedPluginMetadata = { metadata: {}, dropped: [] }
	if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
		return result
	}
	const input = raw as Record<string, unknown>

	// The deprecated name is read only when the new one is absent — an explicit
	// `null` under the new name is an answer, and wins.
	const typesKey =
		"supportedContextTypes" in input
			? "supportedContextTypes"
			: "allowedObjectTypes" in input
				? "allowedObjectTypes"
				: null
	if (typesKey !== null && input[typesKey] !== null) {
		const listed = input[typesKey]
		const known = Array.isArray(listed)
			? [...new Set(listed.filter(isObjectType))]
			: []
		// Never "anywhere": that is `null`'s to say (ADR 0006). A list left with
		// nothing usable is refused whole rather than widened.
		if (known.length > 0) {
			result.metadata.supportedContextTypes = known
		} else {
			result.dropped.push(typesKey)
		}
	}

	for (const key of DIMENSIONS) {
		if (!(key in input)) {
			continue
		}
		const value = input[key]
		if (isUsableDimension(value)) {
			result.metadata[key] = value
		} else {
			result.dropped.push(key)
		}
	}

	return result
}
